import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, ArrowLeft, Check, AlertCircle, Home, Calendar, DollarSign } from 'lucide-react';
import api from '../../api/apiClient';

interface LeaseDetail {
  id: number;
  propertyAddress: string;
  propertyId: number;
  status: string;
  startDate: string;
  endDate: string;
  rentAmount: number;
  rentFrequency: string;
  securityDeposit?: number;
  terms?: string;
  tenantName?: string;
  tenantEmail?: string;
  tenantId?: number;
}

export default function AgentEditLease() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [lease, setLease] = useState<LeaseDetail | null>(null);

  const [form, setForm] = useState({
    startDate: '',
    endDate: '',
    rentAmount: '',
    rentFrequency: 'WEEKLY',
    securityDeposit: '',
    terms: '',
    status: '',
  });

  useEffect(() => {
    if (id) {
      loadLease();
    }
  }, [id]);

  const loadLease = async () => {
    setLoading(true);
    try {
      const response = await api.get<LeaseDetail>(`/agent/leases/${id}`);
      setLease(response);
      setForm({
        startDate: response.startDate?.split('T')[0] || '',
        endDate: response.endDate?.split('T')[0] || '',
        rentAmount: response.rentAmount?.toString() || '',
        rentFrequency: response.rentFrequency || 'WEEKLY',
        securityDeposit: response.securityDeposit?.toString() || '',
        terms: response.terms || '',
        status: response.status || 'ACTIVE',
      });
    } catch (err) {
      console.error('Failed to load lease:', err);
      setError('Failed to load lease details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.startDate || !form.endDate) {
      setError('Please enter lease dates');
      return;
    }

    if (!form.rentAmount) {
      setError('Please enter rent amount');
      return;
    }

    setSaving(true);
    try {
      const data = {
        startDate: form.startDate,
        endDate: form.endDate,
        rentAmount: parseFloat(form.rentAmount),
        rentFrequency: form.rentFrequency,
        securityDeposit: form.securityDeposit ? parseFloat(form.securityDeposit) : null,
        terms: form.terms,
        status: form.status,
      };

      await api.put(`/agent/leases/${id}`, data);
      setSuccess(true);
      setTimeout(() => navigate(`/agent/leases/${id}`), 1500);
    } catch (err: any) {
      console.error('Failed to update lease:', err);
      setError(err.message || 'Failed to update lease');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!lease) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8 text-center">
        <p className="text-gray-600">Lease not found</p>
        <button onClick={() => navigate('/agent/leases')} className="mt-4 text-secondary hover:text-primary">
          Back to Leases
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate(`/agent/leases/${id}`)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <button onClick={() => navigate('/agent/dashboard')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
          <Home className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Edit Lease</h1>
          <p className="text-gray-600">{lease.propertyAddress}</p>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <Check className="w-5 h-5 text-green-600" />
          <span className="text-green-800">Lease updated successfully! Redirecting...</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Lease Info */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-gray-900 mb-2">Current Lease Information</h3>
        <p className="text-sm text-gray-600">Tenant: {lease.tenantName || 'Not assigned'}</p>
        <p className="text-sm text-gray-600">Status: {lease.status}</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
        <div className="space-y-6">
          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
            >
              <option value="PENDING">Pending</option>
              <option value="ACTIVE">Active</option>
              <option value="EXPIRED">Expired</option>
              <option value="TERMINATED">Terminated</option>
            </select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Start Date
              </label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                End Date
              </label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Rent */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <DollarSign className="w-4 h-4 inline mr-1" />
                Rent Amount
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.rentAmount}
                onChange={(e) => setForm({ ...form, rentAmount: e.target.value })}
                placeholder="0.00"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Frequency</label>
              <select
                value={form.rentFrequency}
                onChange={(e) => setForm({ ...form, rentFrequency: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
              >
                <option value="WEEKLY">Weekly</option>
                <option value="FORTNIGHTLY">Fortnightly</option>
                <option value="MONTHLY">Monthly</option>
              </select>
            </div>
          </div>

          {/* Security Deposit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <DollarSign className="w-4 h-4 inline mr-1" />
              Security Deposit (Optional)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.securityDeposit}
              onChange={(e) => setForm({ ...form, securityDeposit: e.target.value })}
              placeholder="0.00"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
            />
          </div>

          {/* Terms */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              Additional Terms (Optional)
            </label>
            <textarea
              value={form.terms}
              onChange={(e) => setForm({ ...form, terms: e.target.value })}
              rows={4}
              placeholder="Any additional terms or conditions..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate(`/agent/leases/${id}`)}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-3 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

